/**
 * AsksSection — the data requests ("asks") that belong to ONE case or ONE
 * conversation, rendered in place instead of behind a door-wide drawer.
 *
 * Scott/Jess check-in 2026-09-08: the 📨 Requests drawer on the Ask tab
 * duplicated the Cases tab and hid the answers below the fold. Phase 2 folds
 * asks into their case (the workspace's detail pane) and, for a chat that has
 * no case yet, into that conversation (the Ask tab). Same card as before —
 * answer-first, reply thread, request-again / dismiss — just where the
 * question was asked.
 */
import { useMemo, useState } from 'react';

import { DataRequestCard } from '../portal/DataRequestCard';
import { isDraft, isHandedPassAlong, isWaitingAsk } from './askKind';
import { DraftAskCard } from './DraftAskCard';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { hiddenDraftsLine, splitAsks } from './asksView';
import styles from './AsksSection.module.css';

export interface AsksSectionProps {
  requests: DoorDataRequestRead[];
  onRequestUpdated: (updated: DoorDataRequestRead) => void;
  /** "Asks on this case" / "Asks in this conversation". */
  title: string;
  /** Start expanded (the workspace); the Ask tab starts folded. */
  defaultOpen?: boolean;
  /** Force open from outside (the "Answer received" banner). */
  forceOpen?: boolean;
  /** Expand the first request with an unseen answer on open. */
  revealUnseen?: boolean;
  /** Leave out these drafts: a Do next desk's Ask button on screen sends
   *  them. A step draft with no such button (on the reader's own desk)
   *  stays listed here with its Send (UX round 2026-10-27). */
  hideDraftIds?: ReadonlySet<string>;
  /**
   * Where these asks live (thread_no / investigation_no): the recorder's
   * targets on a draft's "say why" form, so the row names the thread or
   * case, never the recipient (kanban 529c0851).
   */
  trackContext?: Record<string, string | number | null | undefined>;
}

export function AsksSection({
  requests,
  onRequestUpdated,
  title,
  defaultOpen = true,
  forceOpen = false,
  revealUnseen = true,
  hideDraftIds,
  trackContext,
}: AsksSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [showDismissed, setShowDismissed] = useState(false);
  const { drafts, live, dismissed } = useMemo(
    () => splitAsks(requests, { hideIds: hideDraftIds }),
    [requests, hideDraftIds],
  );
  // Drafts Do next owns: not listed here, but said, so this list never
  // claims nothing is pending while they wait.
  const hiddenDrafts = hideDraftIds ? requests.filter((r) => isDraft(r) && hideDraftIds.has(r.id)).length : 0;
  const unseen = live.filter((r) => r.answer_unseen).length;
  const openCount = live.filter(isWaitingAsk).length;
  const handedCount = live.filter(isHandedPassAlong).length;
  const expanded = open || forceOpen;
  // The first unseen answer is put in front of the reader on open (the
  // drawer's rule) — once, for that request.
  const revealId = revealUnseen && expanded ? (live.find((r) => r.answer_unseen)?.id ?? null) : null;

  if (drafts.length + live.length + dismissed.length === 0) return null;

  return (
    <section className={styles.section} aria-label={title}>
      <button
        type="button"
        className={styles.head}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={expanded}
      >
        <span className={styles.headTitle}>💬 {title}</span>
        <span className={styles.counts}>
          {/* A draft needs a decision before anything happens, so it is the
              first thing the header reports (owner ruling bz2bb6). */}
          {drafts.length > 0 && (
            <span className={styles.countDraft}>
              {drafts.length} to send
            </span>
          )}
          {openCount > 0 && (
            <span className={styles.countOpen}>
              {openCount} waiting
            </span>
          )}
          {handedCount > 0 && (
            <span className={styles.countOpen} data-testid="asks-handed-count">
              {handedCount} passed along
            </span>
          )}
          {unseen > 0 && (
            <span className={styles.countUnseen} aria-label={`${unseen} new answer${unseen > 1 ? 's' : ''}`}>
              {unseen} new answer{unseen > 1 ? 's' : ''}
            </span>
          )}
          <span className={styles.chev} aria-hidden="true">
            {expanded ? '▾' : '▸'}
          </span>
        </span>
      </button>
      {expanded && (
        <div className={styles.body}>
          {drafts.length > 0 && (
            <>
              <p className={styles.draftLead}>
                Merlin drafted {drafts.length === 1 ? 'this' : 'these'}. Nothing has been
                sent — send it, change it first, or throw it away.
              </p>
              {drafts.map((r) => (
                <DraftAskCard key={r.id} request={r} onRequestUpdated={onRequestUpdated} trackContext={trackContext} />
              ))}
            </>
          )}
          {live.length === 0 && drafts.length === 0 && (
            <p className={styles.empty}>{hiddenDraftsLine(hiddenDrafts) ?? 'Every ask here was dismissed.'}</p>
          )}
          {live.map((r) => (
            <DataRequestCard
              key={r.id}
              request={r}
              reveal={r.id === revealId}
              onRequestUpdated={onRequestUpdated}
            />
          ))}
          {dismissed.length > 0 && (
            <>
              <button
                type="button"
                className={styles.showDismissed}
                onClick={() => setShowDismissed((v) => !v)}
              >
                {showDismissed ? 'Hide' : 'Show'} {dismissed.length} dismissed
              </button>
              {showDismissed &&
                dismissed.map((r) => (
                  <DataRequestCard
                    key={r.id}
                    request={r}
                    onRequestUpdated={onRequestUpdated}
                  />
                ))}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default AsksSection;
